import { ErrorMessage, Field, FieldArray } from "formik";
import { Plus, Trash2, Copy, X } from "lucide-react";
import TextError from "../../forms/TextError";
import SelectMultipleWithSearch from "../Admin/Common/SelectMultipleWithSearch";
import { handleKeyPress } from "../../functions/Utils";
import {
  OCEAN_CHARGE_BASIS,
  OCEAN_CHARGE_CALC_TYPE,
  OCEAN_CHARGE_CURRENCY,
  ORIGIN_DESTINATION_CHARGE_TYPE,
} from "../../utils/constant";
import { fmt, grandTotalFor, chargeRateInr, hasConvertibleRate } from "./oceanQuoteMath";

const CELL_INPUT_CLS =
  "h-[38px] px-2.5 text-sm text-gray-800 bg-white border border-gray-200 rounded-lg outline-none w-full focus:border-yellow-400";

const emptyRow = (containerCount) => ({
  charge: "",
  chargeType: "origin",
  calcType: "SubjectTo",
  basis: "Per Container",
  currency: "INR",
  amounts: Array.from({ length: containerCount }, () => 0),
});

// Shared table for both Buy Charges and Sell Charges tabs — `tabKey` selects
// which array of formik state ("buyCharges" | "sellCharges") this instance
// edits. Container columns are driven by `values.containers` (dynamic per
// the actual FCL mix on the query) rather than a fixed 20GP/40GP/40HC set.
// `charge` stores the sw-charges master _id (chargeList option value) —
// same convention the air ManualQuote uses for origin_destination_charges —
// so the backend can resolve GST/ledger config without re-matching on name.
const BuySellChargesTable = ({ formikData, tabKey, chargeList, exchangeRates, onAddContainer, onRemoveContainer, readOnly }) => {
  const { values, setFieldValue } = formikData;
  const rows = values[tabKey] || [];
  const containers = values.containers || [];
  const oppositeKey = tabKey === "buyCharges" ? "sellCharges" : "buyCharges";

  // Rows can each be in a different currency (the Currency column below) —
  // both the per-container Unit Total and the Grand Total are shown in INR,
  // so every row is normalized before summing.
  const colTotals = containers.map((_, ci) => rows.reduce((sum, r) => sum + chargeRateInr(r, ci, exchangeRates), 0));
  const grandTotal = grandTotalFor(rows, containers, exchangeRates);

  // Currencies actually used on a non-zero row that have no admin-configured
  // exchange rate — the Grand Total above silently treats these as 1:1 INR,
  // so flag them rather than let the KAM read a wrong-looking total as real.
  const unconvertedCurrencies = [
    ...new Set(
      rows
        .filter((r) => r.currency && r.currency !== "INR" && (r.amounts || []).some((a) => Number(a) > 0))
        .filter((r) => !hasConvertibleRate(r.currency, exchangeRates))
        .map((r) => r.currency)
    ),
  ];

  // Picking a charge on one side (Buy/Sell) mirrors it onto the other side
  // too — same charge, type/calc-type/basis/currency — so the KAM doesn't
  // have to re-select the same charge twice, just fill in the other side's
  // rate. Only mirrors when that charge isn't already present on the other
  // side, and reuses its first still-blank row rather than piling up an
  // extra one when possible.
  const mirrorChargeToOpposite = (chargeId, row) => {
    const oppositeRows = values[oppositeKey] || [];
    if (oppositeRows.some((r) => r.charge === chargeId)) return;

    const mirroredRow = {
      charge: chargeId,
      chargeType: row.chargeType,
      calcType: row.calcType,
      basis: row.basis,
      currency: row.currency,
      amounts: Array.from({ length: containers.length }, () => 0),
    };

    const blankIndex = oppositeRows.findIndex((r) => !r.charge);
    if (blankIndex >= 0) {
      setFieldValue(`${oppositeKey}.${blankIndex}`, mirroredRow);
    } else {
      setFieldValue(oppositeKey, [...oppositeRows, mirroredRow]);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <span className="text-xs text-gray-500">
          {containers.length} container type{containers.length === 1 ? "" : "s"} on this rate
        </span>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onAddContainer}
              className="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg border border-dashed transition-colors hover:bg-amber-50"
              style={{ borderColor: "#F0C877", color: "#B86E00", background: "transparent" }}
            >
              <Plus size={13} /> Add container type
            </button>
          </div>
        )}
      </div>

      <div
        className="oc-scroll rounded-2xl overflow-hidden"
        style={{ overflowX: "auto", border: "1px solid #EEF0F3" }}
      >
        <div style={{ minWidth: 760 + containers.length * 110 }}>
          {/* Header */}
          <div
            className="grid"
            style={{
              gridTemplateColumns: `1.6fr 0.9fr 1.1fr 1.1fr ${containers.map(() => "1fr").join(" ")} 1fr 60px`,
              background: "#F7F8FA",
              borderBottom: "1px solid #EEF0F3",
            }}
          >
            {["Charge", "Type", "Calc. Type", "Basis"].map((h) => (
              <div key={h} className="px-3 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                {h}
              </div>
            ))}
            {containers.map((c, ci) => (
              <div key={ci} className="px-2 py-2">
                <div className="flex items-center justify-end gap-1 mb-1">
                  <input
                    type="text"
                    value={c.type}
                    onChange={(e) => setFieldValue(`containers.${ci}.type`, e.target.value.toUpperCase())}
                    title="Container type"
                    disabled={readOnly}
                    className="w-16 h-6 text-[11px] font-bold uppercase text-right border border-gray-200 rounded px-1 outline-none"
                  />
                  {!readOnly && containers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => onRemoveContainer(ci)}
                      title="Remove container type"
                      className="text-gray-300 hover:text-red-400"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-end gap-1">
                  <input
                    type="text"
                    value={c.qty}
                    onChange={(e) =>
                      setFieldValue(`containers.${ci}.qty`, e.target.value === "" ? "" : Number(e.target.value))
                    }
                    onKeyPress={handleKeyPress}
                    title="Quantity"
                    disabled={readOnly}
                    className="w-10 h-6 text-[11px] text-right border border-gray-200 rounded px-1 outline-none"
                  />
                  <span className="text-[10px] text-gray-400">qty</span>
                </div>
              </div>
            ))}
            <div className="px-3 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">Currency</div>
            <div className="px-3 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500 text-center">Action</div>
          </div>

          <FieldArray name={tabKey}>
            {({ remove, push, insert }) => (
              <div>
                {rows.map((row, index) => (
                  <div
                    key={index}
                    className="grid items-center hover:bg-gray-50/70 transition-colors"
                    style={{
                      gridTemplateColumns: `1.6fr 0.9fr 1.1fr 1.1fr ${containers.map(() => "1fr").join(" ")} 1fr 60px`,
                      borderBottom: "1px solid #F2F3F5",
                    }}
                  >
                    <div className="px-3 py-2">
                      <Field
                        as={SelectMultipleWithSearch}
                        options={chargeList}
                        width="100%"
                        size="middle"
                        name={`${tabKey}.${index}.charge`}
                        placeholder="Select charge"
                        handleChange={formikData.handleChange}
                        setFieldValue={formikData.setFieldValue}
                        handlePopulateData={(chargeId) => mirrorChargeToOpposite(chargeId, row)}
                        disabled={readOnly}
                      />
                      <ErrorMessage name={`${tabKey}.${index}.charge`} component={TextError} />
                    </div>

                    <div className="px-1.5 py-2">
                      <Field as="select" name={`${tabKey}.${index}.chargeType`} className={CELL_INPUT_CLS} disabled={readOnly}>
                        {ORIGIN_DESTINATION_CHARGE_TYPE.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Field>
                    </div>

                    <div className="px-1.5 py-2">
                      <Field as="select" name={`${tabKey}.${index}.calcType`} className={CELL_INPUT_CLS} disabled={readOnly}>
                        {OCEAN_CHARGE_CALC_TYPE.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Field>
                    </div>

                    <div className="px-1.5 py-2">
                      <Field as="select" name={`${tabKey}.${index}.basis`} className={CELL_INPUT_CLS} disabled={readOnly}>
                        {OCEAN_CHARGE_BASIS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Field>
                    </div>

                    {containers.map((_, ci) => (
                      <div key={ci} className="px-1.5 py-2">
                        <Field
                          type="text"
                          name={`${tabKey}.${index}.amounts.${ci}`}
                          className={`${CELL_INPUT_CLS} text-right`}
                          onKeyPress={handleKeyPress}
                          disabled={readOnly}
                        />
                      </div>
                    ))}

                    <div className="px-1.5 py-2">
                      <Field as="select" name={`${tabKey}.${index}.currency`} className={CELL_INPUT_CLS} disabled={readOnly}>
                        {OCEAN_CHARGE_CURRENCY.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Field>
                    </div>

                    {!readOnly && (
                      <div className="px-1.5 py-2 flex justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => insert(index, { ...row, amounts: [...(row.amounts || [])] })}
                          title="Duplicate"
                          className="w-8 h-8 rounded-lg bg-gray-100 text-gray-400 flex items-center justify-center"
                        >
                          <Copy size={13} />
                        </button>
                        {rows.length > 1 && (
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            title="Remove"
                            className="w-8 h-8 rounded-lg bg-red-50 text-red-300 flex items-center justify-center"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => push(emptyRow(containers.length))}
                    className="w-full flex items-center justify-center gap-2 text-sm font-semibold py-3 transition-colors"
                    style={{ color: "#B86E00", background: "#FFFBF0" }}
                  >
                    <Plus size={15} /> Add charge
                  </button>
                )}
              </div>
            )}
          </FieldArray>

          {/* Totals */}
          <div
            className="grid"
            style={{
              gridTemplateColumns: `4.7fr ${containers.map(() => "1fr").join(" ")} 1fr 60px`,
              background: "#FCFCFD",
              borderTop: "1px solid #EEF0F3",
              borderBottom: "1px solid #EEF0F3",
            }}
          >
            <div className="px-3 py-3 text-sm font-bold text-gray-700">Unit Total</div>
            {colTotals.map((t, ci) => (
              <div key={ci} className="px-2 py-3 text-sm font-bold text-gray-900 text-right">
                {fmt(t)}
              </div>
            ))}
            <div />
            <div />
          </div>

          <div
            className="grid"
            style={{
              gridTemplateColumns: "2.5fr 3.2fr 1fr 60px",
              background: "#FFF9EE",
            }}
          >
            <div className="px-3 py-4 text-sm font-extrabold" style={{ color: "#B86E00" }}>
              Grand Total Shipment
            </div>
            <div className="px-2 py-4 text-right font-extrabold text-gray-900">{fmt(grandTotal)}</div>
            <div className="px-2 py-4 text-xs font-bold" style={{ color: "#B86E00" }}>INR</div>
            <div />
          </div>
        </div>
      </div>

      {unconvertedCurrencies.length > 0 && (
        <p style={{ color: "#b91c1c", marginTop: 8, fontSize: 13, fontWeight: 600 }}>
          ⚠ No exchange rate is configured for {unconvertedCurrencies.join(", ")} — the totals above are
          showing the raw {unconvertedCurrencies.join("/")} amount as if it were INR, not a real
          conversion. Add {unconvertedCurrencies.length > 1 ? "these currencies" : "this currency"} under
          Admin → Exchange Rate to fix the total.
        </p>
      )}

      {formikData?.errors?.[`${tabKey}_error`] && (
        <p style={{ color: "#ef4444", marginTop: 8, fontSize: 13 }}>
          {formikData.errors[`${tabKey}_error`]}
        </p>
      )}
    </div>
  );
};

export default BuySellChargesTable;
